import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../../../services/apiService';
import { fetchJobseekerReport } from '../services/jobseekerReportService';

const DEBOUNCE_MS = 300;

async function handleSessionExpired() {
    await apiService.postRequest('/auth/logout');
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
}

/**
 * Fetches the Jobseekers Report and keeps it in sync with `params`.
 *
 * Two things matter for a filter-driven chart widget that real users will
 * click through quickly over the life of this feature:
 *  - Debounced: rapid filter changes (dropdown clicks, date pickers, the
 *    include/exclude/only toggles) collapse into one request instead of
 *    firing one per click.
 *  - Race-safe: `apiService` has no request cancellation, so a slow
 *    response for a stale filter combination could otherwise land after a
 *    newer one and clobber the UI with outdated data — a request-id guard
 *    discards any response that isn't for the latest params.
 *
 * `params` may be a plain object literal from the caller (no memoization
 * required) — comparison is by value via JSON, not by reference.
 */
export function useJobseekerReport(params, { enabled = true } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const requestIdRef = useRef(0);
    const isFirstRunRef = useRef(true);
    const paramsKey = JSON.stringify(params);

    const runFetch = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchJobseekerReport(JSON.parse(paramsKey));
            if (requestId !== requestIdRef.current) return; // superseded by a newer request

            if (response?.success) {
                setData(response.data);
            } else if (response?.authenticate === false) {
                await handleSessionExpired();
            } else {
                setError(response?.message || 'Unable to load report');
            }
        } catch (e) {
            if (requestId === requestIdRef.current) {
                setError('Unable to load report');
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsKey]);

    useEffect(() => {
        if (!enabled) return undefined;

        // Fetch immediately on mount; debounce subsequent param changes so a
        // user flipping through filters doesn't fire a request per click.
        if (isFirstRunRef.current) {
            isFirstRunRef.current = false;
            runFetch();
            return undefined;
        }

        const timer = setTimeout(runFetch, DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsKey, enabled]);

    return { data, loading, error, refetch: runFetch };
}
